import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AccessTime } from '@mui/icons-material';
import { format } from 'date-fns';

/**
 * Compact table cell showing a stage timestamp plus selfie/store thumbnails.
 *
 * Props:
 *   data  – stage object (or 'NA' / null)
 *   color – accent colour matching the stage
 */
export default function ActivityCell({ data, color }) {
  const isActive = data && data !== 'NA';

  if (!isActive) {
    return <Typography sx={{ fontSize: '0.75rem', color: '#c8d4dc', fontStyle: 'italic' }}>—</Typography>;
  }

  const time   = data.timeReported ? format(new Date(data.timeReported), 'hh:mm a') : null;
  const selfie = data.imageSelfie?.data;
  const store  = data.imageStore?.data;

  return (
    <Box>
      {time && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <AccessTime sx={{ fontSize: 12, color }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color, fontFamily: 'monospace' }}>
            {time}
          </Typography>
        </Box>
      )}

      {(selfie || store) && (
        <Stack direction="row" spacing={0.75}>
          {selfie && (
            <Tooltip title="Selfie — click to open">
              <Box
                component="a" href={selfie} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'block', width: 38, height: 38, borderRadius: '6px',
                  overflow: 'hidden', border: `1.5px solid ${color}`,
                  flexShrink: 0, cursor: 'pointer',
                  '&:hover': { opacity: 0.82, transform: 'scale(1.06)', transition: '0.15s' },
                }}
              >
                <Box component="img" src={selfie} alt="selfie"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </Box>
            </Tooltip>
          )}

          {store && (
            <Tooltip title="Store — click to open">
              <Box
                component="a" href={store} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'block', width: 38, height: 38, borderRadius: '6px',
                  overflow: 'hidden', border: `1.5px solid ${alpha(color, 0.5)}`,
                  flexShrink: 0, cursor: 'pointer',
                  '&:hover': { opacity: 0.82, transform: 'scale(1.06)', transition: '0.15s' },
                }}
              >
                <Box component="img" src={store} alt="store"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </Box>
            </Tooltip>
          )}
        </Stack>
      )}
    </Box>
  );
}
